import React, { useState, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { X } from 'react-feather'
import { useTranslation } from 'react-i18next'
import Modal from '../../components/Modal'
import { ButtonPrimary } from '../../components/Button'
import { Pool } from './types'

const UnstakeModalContent = styled.div`
  padding: 2rem;
  background: linear-gradient(to bottom, #000000, #231f20);
  color: #ffffff;
  width: 100%;
  box-sizing: border-box;
`

const UnstakeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`

const UnstakeTitle = styled.h2`
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
`

const UnstakeCloseButton = styled(X)`
  cursor: pointer;
  color: #ffffff;
  width: 24px;
  height: 24px;
  border: 1px solid #565656;
  border-radius: 50%;
  padding: 0.25rem;

  &:hover {
    color: #e9ad21;
  }
`

const Section = styled.div`
  margin-bottom: 1rem;
  width: 100%;
  box-sizing: border-box;
`

const UnstakeSectionLabel = styled.div`
  color: #e9ad21;
  font-size: 0.9rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
`

const UnstakeHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`

const UnstakeAmountCard = styled.div`
  border-radius: 16px;
  border: 1px solid #565656;
  padding: 1.2rem 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
`

const UnstakeAmountTexts = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.4rem;
  width: 100%;
`

const UnstakeMainAmount = styled.input`
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
  background: transparent;
  border: none;
  outline: none;
  text-align: right;
  width: 100%;

  &::placeholder {
    color: #6c7284;
  }
`

const UnstakeSubAmount = styled.div`
  font-size: 0.85rem;
  color: #b0b5c0;
  text-align: right;
`

const UnstakeTokenInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const UnstakeTokenLogo = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e9ad21;
`

const UnstakeTokenName = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
`

const UnstakeBalanceText = styled.div`
  font-size: 0.85rem;
  color: #b0b5c0;
  margin-bottom: 0.75rem;
`

const UnstakeStakedText = styled.div`
  font-size: 0.85rem;
  color: #b0b5c0;
  margin-bottom: 0.75rem;
`

const UnstakeSliderWrapper = styled.div`
  margin-bottom: 0.25rem;
`

const UnstakeSliderTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: #4b4f5c;
  overflow: hidden;
`

const UnstakeSliderFill = styled.div<{ percent: number }>`
  height: 100%;
  width: ${({ percent }) => `${percent}%`};
  background: #e9ad21;
`

const UnstakeSliderPercent = styled.div`
  font-size: 0.85rem;
  color: #ffffff;
  text-align: right;
  margin-bottom: 1rem;
`

const UnstakePercentButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 3.5rem;
`

const UnstakePercentButton = styled.button`
  flex: 1;
  padding: 0.6rem 0;
  border-radius: 8px;
  border: none;
  background-color: #565656;
  color: #e9ad21;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`

const UnstakeConfirmButton = styled(ButtonPrimary)<{ disabled?: boolean }>`
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 15px;
  background-color: ${({ disabled }) => (disabled ? '#565656' : '#e9ad21')};
  color: ${({ disabled }) => (disabled ? '#999' : '#000')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 0.6 : 0.9)};
  }
`

interface UnstakeModalProps {
  isOpen: boolean
  onDismiss: () => void
  pool: Pool
  unstakeAmount: string
  onUnstakeAmountChange: (value: string) => void
  onConfirm: () => void
  stakedBalance: string
  balance: string
  stakeTokenLogoURL?: string
  stakePrice?: number | null
  userStakedAmount?: string
  isUnstaking?: boolean
}

export default function UnstakeModal({
  isOpen,
  onDismiss,
  pool,
  unstakeAmount,
  onUnstakeAmountChange,
  onConfirm,
  stakedBalance,
  balance,
  stakeTokenLogoURL,
  stakePrice,
  userStakedAmount,
  isUnstaking = false
}: UnstakeModalProps) {
  const { t } = useTranslation()
  const [percent, setPercent] = useState(0)

  const stakeDecimals = pool.stakeTokenDecimals || 18

  const numericStakedBalance = useMemo(() => {
    const v = parseFloat(stakedBalance || '0')
    return Number.isFinite(v) ? v : 0
  }, [stakedBalance])

  const numericBalance = useMemo(() => {
    const v = parseFloat(balance || '0')
    return Number.isFinite(v) ? v : 0
  }, [balance])

  const formattedBalance = useMemo(() => {
    if (!Number.isFinite(numericBalance)) return '0'
    if (numericBalance === 0) return '0'
    if (numericBalance < 0.0001) return '<0.0001'
    const decimals = Math.min(stakeDecimals, 6)
    const factor = 10 ** decimals
    const floored = Math.floor(numericBalance * factor) / factor
    return floored.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals
    })
  }, [numericBalance, stakeDecimals])

  const formattedStakedBalance = useMemo(() => {
    if (!Number.isFinite(numericStakedBalance)) return '0'
    if (numericStakedBalance === 0) return '0'
    if (numericStakedBalance < 0.0001) return '<0.0001'
    const decimals = Math.min(stakeDecimals, 6)
    const factor = 10 ** decimals
    const floored = Math.floor(numericStakedBalance * factor) / factor
    return floored.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals
    })
  }, [numericStakedBalance, stakeDecimals])

  // 计算 USD 金额
  const usdAmount = useMemo(() => {
    if (!unstakeAmount || parseFloat(unstakeAmount) <= 0) return '--'
    if (!stakePrice || stakePrice <= 0) return '--'
    const tokenValue = parseFloat(unstakeAmount)
    const usdValue = tokenValue * stakePrice
    return usdValue.toFixed(6)
  }, [unstakeAmount, stakePrice])

  const handlePercentClick = (p: number) => {
    setPercent(p)
    // 根据百分比计算取消质押数量
    const decimals = Math.min(stakeDecimals, 6)
    const raw = numericStakedBalance * (p / 100)
    const factor = 10 ** decimals
    const floored = Math.floor(raw * factor) / factor
    const amount = floored.toFixed(decimals)
    onUnstakeAmountChange(parseFloat(amount) === 0 ? '' : Math.max(0, parseFloat(amount)).toString())
  }

  // 当输入金额变化时，更新百分比
  useEffect(() => {
    if (!unstakeAmount || numericStakedBalance === 0) {
      setPercent(0)
      return
    }
    const amountValue = parseFloat(unstakeAmount)
    if (amountValue <= 0 || !Number.isFinite(amountValue)) {
      setPercent(0)
      return
    }
    const calculatedPercent = (amountValue / numericStakedBalance) * 100
    setPercent(Math.min(100, Math.max(0, calculatedPercent)))
  }, [unstakeAmount, numericStakedBalance])

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} maxHeight={90}>
      <UnstakeModalContent>
        <UnstakeHeader>
          <UnstakeTitle>{t('yieldFarming.unstake')}</UnstakeTitle>
          <UnstakeCloseButton onClick={onDismiss} />
        </UnstakeHeader>

        <Section>
          <UnstakeHeaderRow>
            <UnstakeSectionLabel>{t('yieldFarming.unstake')}</UnstakeSectionLabel>
            <UnstakeTokenInfo>
              <UnstakeTokenLogo
                style={
                  stakeTokenLogoURL
                    ? {
                        backgroundImage: `url(${stakeTokenLogoURL})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }
                    : undefined
                }
              />
              <UnstakeTokenName>{pool.stakeToken}</UnstakeTokenName>
            </UnstakeTokenInfo>
          </UnstakeHeaderRow>
          <UnstakeAmountCard>
            <UnstakeAmountTexts>
              <UnstakeMainAmount
                type="text"
                placeholder="0.0"
                value={unstakeAmount}
                onChange={e => onUnstakeAmountChange(e.target.value.replace(/[^0-9.]/g, ''))}
              />
              <UnstakeSubAmount>{usdAmount} USD1</UnstakeSubAmount>
            </UnstakeAmountTexts>
          </UnstakeAmountCard>

          <UnstakeBalanceText>
            {t('yieldFarming.balance')}: {formattedBalance} {pool.stakeToken}
          </UnstakeBalanceText>
          {userStakedAmount && (
            <UnstakeStakedText>
              {t('yieldFarming.staked')}: {formattedStakedBalance} {pool.stakeToken}
            </UnstakeStakedText>
          )}

          <UnstakeSliderWrapper>
            <UnstakeSliderTrack>
              <UnstakeSliderFill percent={percent} />
            </UnstakeSliderTrack>
          </UnstakeSliderWrapper>
          <UnstakeSliderPercent>{percent.toFixed(2)}%</UnstakeSliderPercent>

          <UnstakePercentButtons>
            <UnstakePercentButton onClick={() => handlePercentClick(25)}>25%</UnstakePercentButton>
            <UnstakePercentButton onClick={() => handlePercentClick(50)}>50%</UnstakePercentButton>
            <UnstakePercentButton onClick={() => handlePercentClick(75)}>75%</UnstakePercentButton>
            <UnstakePercentButton onClick={() => handlePercentClick(100)}>{t('yieldFarming.max')}</UnstakePercentButton>
          </UnstakePercentButtons>

          <UnstakeConfirmButton onClick={onConfirm} disabled={isUnstaking}>
            {isUnstaking ? t('yieldFarming.unstaking') : t('yieldFarming.confirm')}
          </UnstakeConfirmButton>
        </Section>
      </UnstakeModalContent>
    </Modal>
  )
}
